import { Button } from '@art-of-hospitality-solutions-llc/ui-kit/components/button';
import {
  Card,
  CardContent,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/table';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/tabs';
import {
  toastError,
  toastSuccess,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/toast';
import { Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@Components/AlertDialog';
import Breadcrumbs from '@Components/Breadcrumbs';
import NotFound from '@Components/NotFound';
import ConfirmationDialog from '@Components/ConfirmationDialog';
import ShowFor from '@Components/ShowFor';
import Heading from '@Components/Heading';
import PromoCodeRedemptionList from '@Components/PromoCodes/PromoCodeRedemptionList';
import PromoCodeSheet from '@Components/PromoCodes/PromoCodeSheet';
import { usePromoCode } from '@Hooks/usePromoCode';
import { promoCodeTypeLabels, productTypeLabels } from '@Types/promoCode';
import type { ProductType } from '@Types/promoCode';
import { useDeletePromoCode } from '@Hooks/useDeletePromoCode';
import { useUpdatePromoCode } from '@Hooks/useUpdatePromoCode';

const formatDiscount = (discountType: string, discountValue: number) => {
  if (discountType === 'PERCENTAGE') return `${discountValue}%`;
  if (discountType === 'FLATAMOUNT') return `$${discountValue}`;
  return `$${discountValue} (price override)`;
};

const formatCodeType = (codeType: string) =>
  promoCodeTypeLabels[codeType as keyof typeof promoCodeTypeLabels] ?? codeType;

export default function PromoCodeDetails() {
  const { promoCodeId } = useParams();
  const navigate = useNavigate();
  const {
    data: promoCode,
    status,
    isError,
  } = usePromoCode({
    id: promoCodeId ?? '',
    include: 'promoCodeTicketedEvents.ticketedEvent.venue',
    enabled: !!promoCodeId,
  });

  const [sheetOpen, setSheetOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [removeEventDialogOpen, setRemoveEventDialogOpen] = useState(false);
  const [eventToRemove, setEventToRemove] = useState<{
    id: string;
    name: string;
  } | null>(null);

  const deleteMutation = useDeletePromoCode();
  const updateMutation = useUpdatePromoCode();

  const handleDeleteConfirm = async () => {
    if (!promoCode) return;

    try {
      await deleteMutation.mutateAsync({ id: promoCode.id });
      toastSuccess(`"${promoCode.code}" has been deleted.`);
      setDeleteDialogOpen(false);
      navigate('/promo-codes');
    } catch (err) {
      toastError(
        err instanceof Error ? err.message : 'Failed to delete Promo Code',
      );
    }
  };

  const handleSheetSuccess = () => {
    toastSuccess(`"${promoCode?.code}" has been updated.`);
  };

  const handleRemoveEvent = async () => {
    if (!promoCode || !eventToRemove) return;

    try {
      const ticketedEventIds = (promoCode.promoCodeTicketedEvents ?? [])
        .filter((pe) => pe.ticketedEventId !== eventToRemove.id)
        .map((pe) => pe.ticketedEventId);

      await updateMutation.mutateAsync({
        id: promoCode.id,
        payload: { ticketedEventIds },
      });
      toastSuccess(`"${eventToRemove.name}" has been removed.`);
      setRemoveEventDialogOpen(false);
      setEventToRemove(null);
    } catch (err) {
      toastError(
        err instanceof Error ? err.message : 'Failed to remove ticketed event',
      );
    }
  };

  if (isError) {
    return <NotFound />;
  }
  if (status === 'pending' || !promoCode) {
    return <div>Loading...</div>;
  }

  return (
    <div className="w-full">
      <Breadcrumbs
        navList={[
          { name: 'Admin', href: '/' },
          { name: 'Promo Codes', href: '/promo-codes' },
          { name: promoCode.code, href: `/promo-codes/${promoCodeId}` },
        ]}
      />

      <div className="mt-8 mb-4 flex items-end">
        <Heading>{promoCode.code}</Heading>
        <p className="mx-2 mb-1 text-sm font-bold text-zinc-500">PROMO CODE</p>
        <div className="ml-auto flex items-center space-x-2">
          <ShowFor permissions={['PROMO_CODES_WRITE']}>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setSheetOpen(true)}
            >
              Edit
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="text-red-600 hover:text-red-700"
              onClick={() => setDeleteDialogOpen(true)}
            >
              Delete
            </Button>
          </ShowFor>
        </div>
      </div>

      <Tabs defaultValue="details">
        <TabsList>
          <TabsTrigger value="details">Details</TabsTrigger>
          <TabsTrigger value="redemptions">Redemptions</TabsTrigger>
        </TabsList>

        <TabsContent value="details" className="mt-6">
          <div className="grid grid-cols-1 gap-12 xl:grid-cols-3">
            <div className="flex flex-col space-y-8 xl:col-span-2">
              <div>
                <div className="mb-2">
                  <Heading level="h3">Details</Heading>
                </div>
                <Card>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-x-8 gap-y-4 sm:grid-cols-3">
                      <div>
                        <div className="text-sm font-bold text-zinc-500">
                          Code
                        </div>
                        <span className="font-mono">{promoCode.code}</span>
                      </div>
                      <div className="col-span-2 sm:col-span-3">
                        <div className="text-sm font-bold text-zinc-500">
                          Description
                        </div>
                        {promoCode.description || (
                          <span className="text-zinc-400">No description</span>
                        )}
                      </div>
                      <div>
                        <div className="text-sm font-bold text-zinc-500">
                          Code Type
                        </div>
                        {formatCodeType(promoCode.codeType)}
                      </div>
                      <div>
                        <div className="text-sm font-bold text-zinc-500">
                          Discount
                        </div>
                        {formatDiscount(
                          promoCode.discountType,
                          promoCode.discountValue,
                        )}
                      </div>
                      <div>
                        <div className="text-sm font-bold text-zinc-500">
                          Max Redemptions
                        </div>
                        {promoCode.maxRedemptions ?? (
                          <span className="text-zinc-400">Unlimited</span>
                        )}
                      </div>
                      <div>
                        <div className="text-sm font-bold text-zinc-500">
                          Valid From
                        </div>
                        {new Date(promoCode.validFrom).toLocaleDateString()}
                      </div>
                      <div>
                        <div className="text-sm font-bold text-zinc-500">
                          Valid Until
                        </div>
                        {promoCode.validUntil ? (
                          new Date(promoCode.validUntil).toLocaleDateString()
                        ) : (
                          <span className="text-zinc-400">No expiry</span>
                        )}
                      </div>
                      <div>
                        <div className="text-sm font-bold text-zinc-500">
                          Times Redeemed
                        </div>
                        {promoCode.timesRedeemed}
                      </div>
                      <div>
                        <div className="text-sm font-bold text-zinc-500">
                          Applies To
                        </div>
                        {promoCode.scope && promoCode.scope.length > 0 ? (
                          promoCode.scope
                            .map(
                              (s) =>
                                productTypeLabels[s as ProductType] ?? s,
                            )
                            .join(', ')
                        ) : (
                          <span className="text-zinc-400">
                            All product types
                          </span>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              <div>
                <div className="mb-2">
                  <Heading level="h3">Redemption Statistics</Heading>
                </div>
                <Card>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-x-8 gap-y-4 sm:grid-cols-4">
                      <div>
                        <div className="text-sm font-bold text-zinc-500">
                          Total Discount Given
                        </div>
                        $
                        {promoCode.meta?.stats?.totalDiscountGiven?.toFixed(
                          2,
                        ) ?? '0.00'}
                      </div>
                      <div>
                        <div className="text-sm font-bold text-zinc-500">
                          Avg Discount Amount
                        </div>
                        $
                        {promoCode.meta?.stats?.avgDiscountAmount?.toFixed(2) ??
                          '0.00'}
                      </div>
                      <div>
                        <div className="text-sm font-bold text-zinc-500">
                          By Status
                        </div>
                        <div className="text-sm">
                          Applied:{' '}
                          {promoCode.meta?.stats?.redemptionsByStatus
                            ?.APPLIED ?? 0}
                        </div>
                        <div className="text-sm">
                          Pending:{' '}
                          {promoCode.meta?.stats?.redemptionsByStatus
                            ?.PENDING ?? 0}
                        </div>
                        <div className="text-sm">
                          Reverted:{' '}
                          {promoCode.meta?.stats?.redemptionsByStatus
                            ?.REVERTED ?? 0}
                        </div>
                      </div>
                      <div>
                        <div className="text-sm font-bold text-zinc-500">
                          By Product Type
                        </div>
                        <div className="text-sm">
                          Ticketed Events:{' '}
                          {promoCode.meta?.stats?.redemptionsByProductType
                            ?.TICKETED_EVENT ?? 0}
                        </div>
                        <div className="text-sm">
                          Shop Purchases:{' '}
                          {promoCode.meta?.stats?.redemptionsByProductType
                            ?.SHOP_PURCHASE ?? 0}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              <div>
                <div className="mb-2">
                  <Heading level="h3">Applied To</Heading>
                </div>
                <Card>
                  <CardContent>
                    {promoCode.promoCodeTicketedEvents &&
                    promoCode.promoCodeTicketedEvents.length > 0 ? (
                      <div>
                        <div className="mb-2 text-sm font-bold text-zinc-500">
                          Ticketed Events
                        </div>
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Name</TableHead>
                              <TableHead className="w-[60px]" />
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {promoCode.promoCodeTicketedEvents.map((pe) => (
                              <TableRow key={pe.ticketedEventId}>
                                <TableCell>
                                  <Link
                                    to={`/ticketed-events/${pe.ticketedEventId}`}
                                    className="text-primary hover:underline"
                                  >
                                    {pe.ticketedEvent?.name ??
                                      pe.ticketedEventId}
                                  </Link>
                                  {pe.ticketedEvent?.venue && (
                                    <div className="text-sm text-zinc-500">
                                      {pe.ticketedEvent.venue.name}
                                    </div>
                                  )}
                                </TableCell>
                                <TableCell>
                                  <ShowFor permissions={['PROMO_CODES_WRITE']}>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => {
                                        setEventToRemove({
                                          id: pe.ticketedEventId,
                                          name:
                                            pe.ticketedEvent?.name ??
                                            pe.ticketedEventId,
                                        });
                                        setRemoveEventDialogOpen(true);
                                      }}
                                    >
                                      <Trash2 className="h-4 w-4 text-red-500" />
                                    </Button>
                                  </ShowFor>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    ) : (
                      <div className="py-4 text-center text-sm text-zinc-500">
                        This promo code is not applied to any resources.
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </div>

            <div className="flex flex-col space-y-8 xl:col-span-1">
              <div className="sticky top-4">
                <Card>
                  <CardContent>
                    <div className="flex flex-col space-y-4">
                      <div>
                        <div className="text-sm font-bold text-zinc-500">
                          Status
                        </div>
                        <span
                          className={
                            promoCode.isActive
                              ? 'font-medium text-green-600'
                              : 'text-zinc-400'
                          }
                        >
                          {promoCode.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                      <div>
                        <div className="text-sm font-bold text-zinc-500">
                          Discount
                        </div>
                        {formatDiscount(
                          promoCode.discountType,
                          promoCode.discountValue,
                        )}
                      </div>
                      <div>
                        <div className="text-sm font-bold text-zinc-500">
                          Code Type
                        </div>
                        {formatCodeType(promoCode.codeType)}
                      </div>
                      <div>
                        <div className="text-sm font-bold text-zinc-500">
                          Usage
                        </div>
                        {promoCode.timesRedeemed}
                        {promoCode.maxRedemptions
                          ? ` / ${promoCode.maxRedemptions}`
                          : ' / Unlimited'}
                      </div>
                      <div>
                        <div className="text-sm font-bold text-zinc-500">
                          Valid From
                        </div>
                        {new Date(promoCode.validFrom).toLocaleDateString()}
                      </div>
                      <div>
                        <div className="text-sm font-bold text-zinc-500">
                          Valid Until
                        </div>
                        {promoCode.validUntil ? (
                          new Date(promoCode.validUntil).toLocaleDateString()
                        ) : (
                          <span className="text-zinc-400">No expiry</span>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="redemptions" className="mt-12">
          <PromoCodeRedemptionList promoCodeId={promoCode.id} />
        </TabsContent>
      </Tabs>

      <PromoCodeSheet
        promoCode={promoCode}
        isOpen={sheetOpen}
        setIsOpen={setSheetOpen}
        onSuccess={handleSheetSuccess}
      />

      <ConfirmationDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onSubmit={handleDeleteConfirm}
        match={promoCode.code}
        title="Delete Promo Code"
        description={`Are you sure you want to delete "${promoCode.code}"? This action cannot be undone.`}
        matchLabel="Type the promo code to confirm"
        submitLabel="Delete"
        isLoading={deleteMutation.isPending}
        variant="destructive"
      />

      <AlertDialog
        open={removeEventDialogOpen}
        onOpenChange={setRemoveEventDialogOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Ticketed Event</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove {eventToRemove?.name} from this
              promo code?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive hover:bg-destructive/90 text-white shadow-xs"
              onClick={handleRemoveEvent}
            >
              Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
